import { Component, Input, forwardRef, OnDestroy } from '@angular/core';
import { COMMA, SEMICOLON, ENTER, TAB } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CustomValidators } from '../validators/custom-validators';

@Component({
	selector: 'rpshared-multi-chip-input',
	templateUrl: './multi-chip-input.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MultiChipInputComponent),
			multi: true,
		},
	],
})
export class MultiChipInputComponent
	implements ControlValueAccessor, OnDestroy
{
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}

	emailListInput: FormControl = new FormControl('');
	private _isEmailList = false;
	@Input()
	get isEmailList(): boolean {
		return this._isEmailList;
	}
	set isEmailList(value: boolean) {
		this._isEmailList = coerceBooleanProperty(value);
		this.emailListInput.setValidators(CustomValidators.isEmailListInvalid);
	}

	private _defaultIfEmpty: string;
	@Input() set defaultIfEmpty(value: string) {
		this._defaultIfEmpty = value;
		if (this._defaultIfEmpty && (!this.chips || this.chips.length === 0)) {
			this.chips.push(value);
		}
	}

	@Input() placeholder: string;

	chips: string[] = [];
	readonly separatorKeysCodes = [ENTER, COMMA, SEMICOLON, TAB];

	removable = true;

	writeValue(value: string[]): void {
		if (value && value.length > 0 && value.every((v) => v.trim())) {
			this.chips = value;
		} else if (this._defaultIfEmpty) {
			this.chips = [this._defaultIfEmpty];
		} else {
			this.chips = [];
		}
	}

	// view --> model
	registerOnChange(fn: (value: string[]) => void) {
		this.onChanged = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};
	onChanged: (value: string[]) => void = () => {};

	setDisabledState?(isDisabled: boolean) {
		this.removable = !isDisabled;
	}

	add(event: MatChipInputEvent): void {
		const input = event.chipInput;
		const value = event.value;

		if ((value || '').trim()) {
			this.chips.push(value);
			this.onChanged(this.chips);
		}

		input.clear();
	}

	remove(input: string): void {
		const index = this.chips.indexOf(input);

		if (index >= 0) {
			this.chips.splice(index, 1);
			this.onChanged(this.chips);
		}
	}

	ngOnDestroy() {
		this.chips = [];
		this.onChanged(this.chips);
		this._defaultIfEmpty = undefined;
		this.required = false;
	}
}
